import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { timeToMinutes } from '@/shared/utils/time';
import { fetchPastPerformanceStream } from '../../services/dashboardAPI';
import { getApiErrorMessage } from '../../services/errorUtils';
import type { PastPerformanceDailyRow, PastPerformanceResponse } from '../../schemas/apiResponse.schemas';
import './AdminDashboard.css';

type DatePreset = 7 | 30 | 90;

function formatDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function subtractDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() - days);
  return next;
}

function getPresetRange(days: DatePreset): { start: string; end: string } {
  const end = new Date();
  const start = subtractDays(end, days - 1);
  return {
    start: formatDateInputValue(start),
    end: formatDateInputValue(end),
  };
}

function formatAverage(value: number, fractionDigits = 1): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function formatTime(value: string | null): string {
  return value ?? '—';
}

function formatDateWithWeekday(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    weekday: 'short',
    timeZone: 'UTC',
  });
  return `${date} (${weekday})`;
}

function computeHoursMinusBreak(
  earliestShipTime: string | null,
  latestShipTime: string | null,
): number | null {
  if (!earliestShipTime || !latestShipTime) {
    return null;
  }
  return (timeToMinutes(latestShipTime) - timeToMinutes(earliestShipTime)) / 60 - 1;
}

function formatHoursMinusBreak(
  earliestShipTime: string | null,
  latestShipTime: string | null,
): string {
  const hours = computeHoursMinusBreak(earliestShipTime, latestShipTime);
  return hours == null ? '—' : formatAverage(hours);
}

function formatOrdersPerHour(row: PastPerformanceDailyRow): string {
  const hours = computeHoursMinusBreak(row.earliestShipTime, row.latestShipTime);
  if (hours == null || hours <= 0) {
    return '—';
  }
  return formatAverage(row.ordersShipped / hours);
}

function computeRangeHoursAverages(rows: PastPerformanceDailyRow[]): {
  avgHoursPerDay: number | null;
  avgOrdersPerHour: number | null;
} {
  const hoursValues: number[] = [];
  const ordersPerHourValues: number[] = [];

  for (const row of rows) {
    const hours = computeHoursMinusBreak(row.earliestShipTime, row.latestShipTime);
    if (hours == null || hours <= 0) {
      continue;
    }
    hoursValues.push(hours);
    ordersPerHourValues.push(row.ordersShipped / hours);
  }

  return {
    avgHoursPerDay:
      hoursValues.length > 0
        ? hoursValues.reduce((sum, hours) => sum + hours, 0) / hoursValues.length
        : null,
    avgOrdersPerHour:
      ordersPerHourValues.length > 0
        ? ordersPerHourValues.reduce((sum, rate) => sum + rate, 0) / ordersPerHourValues.length
        : null,
  };
}

const PastPerformance: React.FC = () => {
  const navigate = useNavigate();
  const initialRange = useMemo(() => getPresetRange(30), []);
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [activePreset, setActivePreset] = useState<DatePreset | null>(30);
  const [data, setData] = useState<PastPerformanceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasRun, setHasRun] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  const loadReport = useCallback(async (start: string, end: string): Promise<void> => {
    if (!start || !end || start > end) {
      setError('Choose a valid start and end date.');
      return;
    }

    setLoading(true);
    setError(null);
    setHasRun(true);
    setProgressPercent(0);
    setProgressMessage('Starting report…');
    try {
      const response = await fetchPastPerformanceStream({ start, end }, (event) => {
        const percent = event.total > 0 ? (event.current / event.total) * 100 : 0;
        setProgressPercent(percent);
        setProgressMessage(event.message);
      });
      setProgressPercent(100);
      setData(response);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to load past performance report'));
      setData(null);
    } finally {
      setLoading(false);
      setProgressMessage('');
    }
  }, []);

  const applyPreset = (days: DatePreset): void => {
    const range = getPresetRange(days);
    setActivePreset(days);
    setStartDate(range.start);
    setEndDate(range.end);
  };

  const activeDailyRows = useMemo(
    () => (data?.daily ?? []).filter((row) => row.ordersShipped > 0),
    [data],
  );

  const visibleTags = useMemo(
    () =>
      (data?.tags ?? []).filter((tag) =>
        (data?.daily ?? []).some((row) => (row.tagCounts[tag] ?? 0) > 0),
      ),
    [data],
  );

  const rangeHoursAverages = useMemo(
    () => computeRangeHoursAverages(activeDailyRows),
    [activeDailyRows],
  );

  return (
    <div className="admin-dashboard-container">
      <div className="admin-dashboard-header">
        <button
          type="button"
          className="admin-dashboard-back"
          onClick={() => navigate('/admin')}
        >
          Back to Admin Panel
        </button>
        <p className="admin-dashboard-subtitle">
          Shipstation shipped orders by label-created date. Earliest/latest times are from label
          creation. Tag columns show warehouse workers only.
        </p>
      </div>

      <section className="admin-dashboard-section">
        <h2>Time interval</h2>
        <div className="admin-dashboard-presets">
          {([7, 30, 90] as const).map((days) => (
            <button
              key={days}
              type="button"
              className={`admin-dashboard-preset${activePreset === days ? ' active' : ''}`}
              onClick={() => applyPreset(days)}
            >
              Last {days} days
            </button>
          ))}
        </div>
        <div className="admin-dashboard-date-range">
          <label>
            Start
            <input
              type="date"
              value={startDate}
              onChange={(event) => {
                setActivePreset(null);
                setStartDate(event.target.value);
              }}
            />
          </label>
          <label>
            End
            <input
              type="date"
              value={endDate}
              onChange={(event) => {
                setActivePreset(null);
                setEndDate(event.target.value);
              }}
            />
          </label>
        </div>
        <button
          type="button"
          className="admin-dashboard-preset"
          disabled={loading}
          onClick={() => void loadReport(startDate, endDate)}
        >
          Run Report
        </button>
      </section>

      {!hasRun && !loading && (
        <p className="admin-dashboard-status">Choose a date range and click Run Report.</p>
      )}
      {loading && (
        <div className="admin-dashboard-progress-container">
          <div className="admin-dashboard-progress-bar">
            <div
              className="admin-dashboard-progress-fill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="admin-dashboard-progress-text">
            {progressMessage || 'Loading report…'}
          </p>
        </div>
      )}
      {error && <p className="admin-dashboard-error">{error}</p>}

      {data && !loading && (
        <>
          <section className="admin-dashboard-section">
            <h2>Range averages</h2>
            <p className="admin-dashboard-subtitle">
              Averages use {data.averages.activeDays} day{data.averages.activeDays === 1 ? '' : 's'} with
              at least one shipped order (zero-order days excluded).
            </p>
            <div className="admin-dashboard-table-wrap">
              <table className="admin-dashboard-table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Average</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Orders shipped / day</td>
                    <td>{formatAverage(data.averages.avgOrdersPerDay)}</td>
                  </tr>
                  <tr>
                    <td>Earliest ship time</td>
                    <td>{formatTime(data.averages.avgEarliestShipTime)}</td>
                  </tr>
                  <tr>
                    <td>Latest ship time</td>
                    <td>{formatTime(data.averages.avgLatestShipTime)}</td>
                  </tr>
                  <tr>
                    <td>Avg hours per day</td>
                    <td>
                      {rangeHoursAverages.avgHoursPerDay == null
                        ? '—'
                        : formatAverage(rangeHoursAverages.avgHoursPerDay)}
                    </td>
                  </tr>
                  <tr>
                    <td>Avg orders per hour</td>
                    <td>
                      {rangeHoursAverages.avgOrdersPerHour == null
                        ? '—'
                        : formatAverage(rangeHoursAverages.avgOrdersPerHour)}
                    </td>
                  </tr>
                  {data.tags.map((tagName) => (
                    <tr key={tagName}>
                      <td>{tagName} / day</td>
                      <td>{formatAverage(data.averages.avgTagCounts[tagName] ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="admin-dashboard-section">
            <h2>Daily breakdown</h2>
            <p className="admin-dashboard-subtitle">
              Orders with multiple worker tags are counted once per tag. Days with no shipments are
              hidden. Worker columns with zero tagged orders in the range are hidden.
            </p>
            <div className="admin-dashboard-table-wrap">
              <table className="admin-dashboard-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Orders</th>
                    <th>Earliest</th>
                    <th>Latest</th>
                    <th>Hours minus break</th>
                    <th>Orders/hour</th>
                    {visibleTags.map((tagName) => (
                      <th key={tagName}>{tagName}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activeDailyRows.length === 0 ? (
                    <tr>
                      <td colSpan={6 + visibleTags.length}>No shipped orders in this range.</td>
                    </tr>
                  ) : (
                    activeDailyRows.map((row) => (
                      <tr key={row.date}>
                        <td>{formatDateWithWeekday(row.date)}</td>
                        <td>{row.ordersShipped}</td>
                        <td>{formatTime(row.earliestShipTime)}</td>
                        <td>{formatTime(row.latestShipTime)}</td>
                        <td>{formatHoursMinusBreak(row.earliestShipTime, row.latestShipTime)}</td>
                        <td>{formatOrdersPerHour(row)}</td>
                        {visibleTags.map((tagName) => (
                          <td key={tagName}>{row.tagCounts[tagName] ?? 0}</td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default PastPerformance;
